import BackLinkButton from '@/components/atoms/BackLinkButton';
import Button from '@/components/atoms/Button';
import Heading from '@/components/atoms/Heading';
import { AnalyticsData } from '@/components/templates/AnalyticsData';
import Breadcrumb from '@/components/templates/Breadcrumb';
import {
  IdPasswordChangeFormState,
  IdPasswordChangeFormType,
} from '@/states/idPasswordChangeFormState';
import { LoginInfoState } from '@/states/loginInfoState';
import { toHankaku } from '@/utils/commonFunc';
import url from '@/utils/config';
import { yupResolver } from '@hookform/resolvers/yup';
import type { NextPage } from 'next';
import Router from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as yup from 'yup';

/**
 * ユーザーID/パスワード変更入力画面。
 * 変更対象の有無や新しい認証情報を入力させ、確認画面へ渡すための state を組み立てる。
 */
const IdPasswordChange: NextPage = () => {
  const loginInfo = useRecoilValue(LoginInfoState);
  const [idPasswordChangeForm, setIdPasswordChangeForm] = useRecoilState(IdPasswordChangeFormState);
  const [isNoChangeUserId, setIsNoChangeUserId] = useState(idPasswordChangeForm.not_change_user_id);
  const [isExpandNote, setIsExpandNote] = useState<boolean>(false);

  // ユーザーID変更有無に応じて条件付きで検証内容を切り替える
  const schema = yup.object().shape({
    new_user_id: yup
      .string()
      .test('userIdrequired', 'ユーザーIDを入力してください。', (value) => {
        if (isNoChangeUserId) {
          // ユーザーIDを変更しないにチェックが入っている場合はバリデーションをスルー
          return true;
        }
        if (typeof value !== 'undefined' && value !== '') {
          return true;
        }
        return false;
      })
      .test('userIdMatchs', 'ユーザーIDの文字列が不正です。入力内容をご確認ください。', (value) => {
        const regex =
          /^(?=.*[0-9a-zA-Z])(?!.*[.@]{2,})[0-9a-zA-Z][0-9a-zA-Z-_.@]{4,62}[0-9a-zA-Z-_@]$/;
        const userId = toHankaku(value ?? '');
        if (isNoChangeUserId) {
          // ユーザーIDを変更しないにチェックが入っている場合はバリデーションをスルー
          return true;
        }
        if (regex.test(userId)) {
          return true;
        }
        return false;
      }),
    current_password: yup.string().required('パスワードを入力してください。'),
    new_password: yup
      .string()
      .required('パスワードを入力してください。')
      .test('invalid', 'パスワードの文字列が不正です。入力内容をご確認ください。', (value) => {
        const newPassword = toHankaku(value ?? '');
        const pattern = /^(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z][0-9a-zA-Z-_]{7,1023}$/;
        if (!pattern.test(newPassword)) {
          return false;
        }
        return true;
      })
      .test(
        'isMatchUserId',
        'パスワードはユーザーIDと同じに設定することはできません。',
        (value) => {
          const newPassword = toHankaku(value ?? '');

          // ユーザーIDを変更しない場合は現在のユーザーIDと比較する
          if (isNoChangeUserId && loginInfo.userId === newPassword) {
            return false;
          }
          // ユーザーIDを変更する場合は新しいユーザーIDと比較する
          if (!isNoChangeUserId && idPasswordChangeForm.new_user_id === newPassword) {
            return false;
          }
          return true;
        }
      ),
    verify_password: yup
      .string()
      .required('パスワードを入力してください。')
      .test('invalid', 'パスワードの文字列が不正です。入力内容をご確認ください。', (value) => {
        const verifyPassword = toHankaku(value ?? '');
        const pattern = /^(?=.*[0-9])(?=.*[a-zA-Z])[0-9a-zA-Z][0-9a-zA-Z-_]{7,1023}$/;
        if (!pattern.test(verifyPassword)) {
          return false;
        }
        return true;
      })
      .test(
        'matches',
        'パスワードとパスワード（確認）が一致しません。入力内容をご確認ください。',
        (value) => {
          const verifyPassword = toHankaku(value ?? '');
          if (idPasswordChangeForm.new_password !== verifyPassword) {
            return false;
          }
          return true;
        }
      ),
  });

  const {
    register,
    trigger,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<IdPasswordChangeFormType>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  /** 入力値をそのままフォーム state に反映する */
  const updateIdPasswordForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setIdPasswordChangeForm({ ...idPasswordChangeForm, [id]: value });
  };

  /** 入力値を半角へ正規化してフォーム state に反映する */
  const updateIdPasswordChangeForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setIdPasswordChangeForm({ ...idPasswordChangeForm, [id]: toHankaku(value) });
  };

  /**
   * ユーザーIDを変更しないチェックの切り替え処理。
   * チェック状態に応じて新ユーザーIDの入力値とバリデーション状態を同期する。
   */
  const handleChangeIsNoChangeUserId = () => {
    setIdPasswordChangeForm({ ...idPasswordChangeForm, new_user_id: '' });
    if (idPasswordChangeForm.new_password !== '') {
      trigger('new_password');
    }
    if (isNoChangeUserId) {
      setError('new_user_id', { type: 'required' });
    } else {
      clearErrors('new_user_id');
    }
    setIsNoChangeUserId(!isNoChangeUserId);
  };

  /** 現在の入力状態を保存し、確認画面へ遷移する */
  const handleClickSubmitButton = () => {
    setIdPasswordChangeForm({ ...idPasswordChangeForm, not_change_user_id: isNoChangeUserId });
    Router.push('/contract/id_password/confirm');
  };

  return (
    <>
      <AnalyticsData />
      <main className='l-main'>
        <Heading as='h1'>ユーザーID/パスワードの変更</Heading>
        <div className='m-box-02'>
          <div className='m-box-02__inner'>
            <p className='m-text'>ユーザーID/パスワードを変更します。</p>
            <a
              className='m-link'
              href='#'
              rel='noreferrer'
              style={{ textDecoration: 'none' }}
              onClick={(e): void => {
                e.preventDefault();
                setIsExpandNote(!isExpandNote);
              }}
              data-testid='expand_note'
            >
              {isExpandNote ? (
                <img
                  className='m-link__img'
                  src={url('/images/icon_arrow_under.svg')}
                  alt='注意事項を非表示にします'
                />
              ) : (
                <img
                  className='m-link__img'
                  src={url('/images/icon_arrow_right.svg')}
                  alt='注意事項を表示します'
                />
              )}
              入力前にご確認ください
            </a>
            {isExpandNote ? (
              <div role='alert' aria-live='polite'>
                <br />
                <p>ユーザーIDは半角英数字6～64文字で入力してください。</p>
                <ul className='m-list'>
                  <li className='m-list__item'>
                    記号「@」(アットマーク)、「-」(ハイフン)、「_」(アンダーバー)、「.」(ドット)も使用できます。
                  </li>
                  <li className='m-list__item'>先頭に記号は使用できません。</li>
                  <li className='m-list__item'>末尾に「.」(ドット)は使用できません。</li>
                  <li className='m-list__item'>
                    連続した「@」(アットマーク)、「.」(ドット)は使用できません。
                  </li>
                </ul>
                <p>パスワードは半角英数字8文字以上で入力してください。</p>
                <ul className='m-list'>
                  <li className='m-list__item'>
                    記号「-」(ハイフン)、「_」(アンダーバー)が使用可です。
                  </li>
                  <li className='m-list__item'>先頭に記号は使用できません。</li>
                  <li className='m-list__item'>必ずアルファベットと数字の両方を含めてください。</li>
                  <li className='m-list__item'>ユーザーIDと同じに設定することはできません。</li>
                </ul>
              </div>
            ) : (
              <br />
            )}

            <br />
            <form
              className='m-form'
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className='m-form__item'>
                <label className='m-form__title' htmlFor='user_id'>
                  <span className='m-form__titleTxt'>ユーザーID</span>
                </label>
                <div className='m-form__lyt'>
                  <input
                    type='text'
                    id='user_id'
                    className='m-form-text'
                    value={loginInfo.userId}
                    disabled
                  />
                </div>
              </div>
              <div className='m-form__item'>
                <input
                  type='checkbox'
                  id='isNoChangeUserId'
                  checked={isNoChangeUserId}
                  onChange={handleChangeIsNoChangeUserId}
                  data-testid='isNoChangeUserId'
                />
                <label htmlFor='isNoChangeUserId'>
                  <span className='m-form__titleTxt'>
                    ユーザーIDを変更しない場合はチェックしてください
                  </span>
                </label>
                <label className='m-form__title' htmlFor='new_user_id'>
                  <span className='m-form__titleTxt'>新しいユーザーID</span>
                </label>
                {errors.new_user_id && (
                  <p className='m-text' role='alert'>
                    <strong data-testid='new_user_id_error'>{errors.new_user_id.message}</strong>
                  </p>
                )}
                <div className='m-form__lyt'>
                  <input
                    type='text'
                    id='new_user_id'
                    className='m-form-text'
                    value={idPasswordChangeForm.new_user_id}
                    disabled={isNoChangeUserId}
                    {...register('new_user_id', {
                      onChange: (e) => {
                        updateIdPasswordForm(e);
                      },
                      onBlur: (e) => {
                        updateIdPasswordChangeForm(e);
                      },
                    })}
                    data-testid='new_user_id'
                  />
                </div>
              </div>
              <div className='m-form__item'>
                <label className='m-form__title' htmlFor='current_password'>
                  <span className='m-form__titleTxt'>現在のパスワード</span>
                  <span className='m-form__label'>必須</span>
                </label>
                {errors.current_password && (
                  <p className='m-text' role='alert'>
                    <strong data-testid='current_password_error'>
                      {errors.current_password.message}
                    </strong>
                  </p>
                )}
                <div className='m-form__lyt'>
                  <input
                    type='password'
                    id='current_password'
                    className='m-form-text'
                    value={idPasswordChangeForm.current_password}
                    {...register('current_password')}
                    onChange={updateIdPasswordForm}
                    data-testid='current_password'
                  />
                </div>
              </div>
              <div className='m-form__item'>
                <label className='m-form__title' htmlFor='new_password'>
                  <span className='m-form__titleTxt'>新しいパスワード</span>
                  <span className='m-form__label'>必須</span>
                </label>
                {errors.new_password && (
                  <p className='m-text' role='alert'>
                    <strong data-testid='new_password_error'>{errors.new_password.message}</strong>
                  </p>
                )}
                <div className='m-form__lyt'>
                  <input
                    type='password'
                    id='new_password'
                    className='m-form-text'
                    value={idPasswordChangeForm.new_password}
                    {...register('new_password', {
                      onChange: (e) => {
                        updateIdPasswordForm(e);
                      },
                      onBlur: (e) => {
                        updateIdPasswordChangeForm(e);
                      },
                    })}
                    data-testid='new_password'
                  />
                </div>
              </div>
              <div className='m-form__item'>
                <label className='m-form__title' htmlFor='verify_password'>
                  <span className='m-form__titleTxt'>新しいパスワード(確認用)</span>
                  <span className='m-form__label'>必須</span>
                </label>
                {errors.verify_password && (
                  <p className='m-text' role='alert'>
                    <strong data-testid='verify_password_error'>
                      {errors.verify_password.message}
                    </strong>
                  </p>
                )}
                <div className='m-form__lyt'>
                  <input
                    type='password'
                    id='verify_password'
                    className='m-form-text'
                    value={idPasswordChangeForm.verify_password}
                    {...register('verify_password', {
                      onChange: (e) => {
                        updateIdPasswordForm(e);
                      },
                      onBlur: (e) => {
                        updateIdPasswordChangeForm(e);
                      },
                    })}
                    data-testid='verify_password'
                  />
                </div>
              </div>

              <div className='m-button col2'>
                <BackLinkButton href='/contract'>戻る</BackLinkButton>
                <Button
                  type='button'
                  variant='secondary'
                  onClick={handleSubmit(handleClickSubmitButton)}
                >
                  変更する
                </Button>
              </div>
            </form>
          </div>
        </div>
        <Breadcrumb />
      </main>
    </>
  );
};

export default IdPasswordChange;
